/**
 * Talking to Supabase. The approved flow, unchanged in kind from the live app:
 * OAuth and an emailed magic link, tokens returned in the URL fragment.
 *
 * What is different is where the result is kept — see api/session.ts. There is
 * no dev-token path and no custom authentication of our own.
 *
 * OAuth goes through the official client rather than a hand-built URL. That is
 * not cosmetic: the old approach silently omitted Azure's `email` scope, and a
 * token with no email claim is an account we cannot identify. The client takes
 * scopes as an argument, so the requirement is visible in the call instead of
 * being something a future reader has to know to append.
 */
import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import { getPublicConfig } from '../../api/client'
import { setSession } from '../../api/session'

function base(): string {
  return (getPublicConfig()?.supabase_url ?? '').replace(/\/$/, '')
}

function anonKey(): string {
  return getPublicConfig()?.supabase_anon_key ?? ''
}

/** Providers we offer, and the extra scopes each one needs. */
const PROVIDERS = {
  google: { label: 'Google', scopes: undefined },
  // Azure defaults to `openid` alone, which returns a token with no email
  // claim. `email` is how a DealPulse user is identified — provisioning cannot
  // create an account without one — so it has to be asked for explicitly.
  azure: { label: 'Microsoft', scopes: 'email profile' },
} as const

export type OAuthProvider = keyof typeof PROVIDERS

let client: SupabaseClient | null = null
let clientUrl = ''

/**
 * The client exists only to broker the OAuth handshake. Every piece of session
 * management it offers is switched off on purpose:
 *
 *   - `persistSession` would write the whole session, access token included, to
 *     localStorage — undoing the storage decision in docs/16 section 3.3, where
 *     the access token stays in memory and only the refresh token is at rest;
 *   - `detectSessionInUrl` would race consumeAuthCallback() for the fragment;
 *   - `autoRefreshToken` would run a second refresh loop beside api/session.ts.
 *
 * `flowType` is pinned rather than left to the default. It is 'implicit' today,
 * which is what returns tokens in the fragment; a future release flipping the
 * default to PKCE would return `?code=` instead and break the callback quietly.
 */
function oauthClient(): SupabaseClient {
  const url = base()
  if (!url) throw new Error('Authentication is not configured on the server.')
  if (!client || clientUrl !== url) {
    client = createClient(url, anonKey(), {
      auth: {
        flowType: 'implicit',
        persistSession: false,
        autoRefreshToken: false,
        detectSessionInUrl: false,
      },
    })
    clientUrl = url
  }
  return client
}

/**
 * Where Supabase sends the browser back to. Must be allowlisted in Supabase.
 *
 * The TRAILING SLASH is deliberate. Supabase returns the tokens in the URL
 * fragment; if the callback landed on "/app" the server would redirect to
 * "/app/" and we would be relying on the browser re-attaching the fragment
 * across a 301. Landing on the canonical path avoids the question entirely.
 */
export function redirectTarget(): string {
  return `${window.location.origin}${import.meta.env.BASE_URL}`
}

/**
 * The provider the user last chose, remembered across the redirect so a failure
 * coming back can name itself. sessionStorage may be unavailable (private mode);
 * a forgotten provider costs a less specific message, nothing more.
 */
const PROVIDER_KEY = 'dealpulse.oauth.provider'

function rememberProvider(provider: OAuthProvider): void {
  try {
    sessionStorage.setItem(PROVIDER_KEY, provider)
  } catch {
    // Non-fatal: see above.
  }
}

function lastProviderLabel(): string {
  try {
    const stored = sessionStorage.getItem(PROVIDER_KEY)
    if (stored && stored in PROVIDERS) return PROVIDERS[stored as OAuthProvider].label
  } catch {
    // Non-fatal: see above.
  }
  return ''
}

/** Build the authorize URL without navigating. Exported for tests. */
export async function buildOAuthUrl(provider: OAuthProvider): Promise<string> {
  const { data, error } = await oauthClient().auth.signInWithOAuth({
    provider,
    options: {
      scopes: PROVIDERS[provider].scopes,
      redirectTo: redirectTarget(),
      skipBrowserRedirect: true,
    },
  })
  if (error) throw new Error(error.message)
  if (!data.url) throw new Error('Could not start sign-in.')
  return data.url
}

export async function startOAuthLogin(provider: OAuthProvider): Promise<void> {
  const url = await buildOAuthUrl(provider)
  rememberProvider(provider)
  window.location.href = url
}

export function startGoogleLogin(): Promise<void> {
  return startOAuthLogin('google')
}

export function startMicrosoftLogin(): Promise<void> {
  return startOAuthLogin('azure')
}

export async function requestMagicLink(email: string): Promise<void> {
  const url = base()
  if (!url) throw new Error('Authentication is not configured on the server.')
  const res = await fetch(`${url}/auth/v1/otp?redirect_to=${encodeURIComponent(redirectTarget())}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', apikey: anonKey() },
    body: JSON.stringify({ email, create_user: true }),
  })
  if (!res.ok) {
    const payload = (await res.json().catch(() => ({}))) as { msg?: string; error_description?: string }
    throw new Error(payload.msg ?? payload.error_description ?? `Could not send the link (error ${res.status}).`)
  }
}

/**
 * Turn a provider's refusal into something a person can act on.
 *
 * Every message ends with a route out, because the emailed link works even when
 * a provider does not — and the commonest failure here is an organisation that
 * has not approved the app, which the user cannot fix alone.
 */
export function oauthErrorMessage(params: URLSearchParams): string {
  const fallback = ' You can use the email sign-in link below instead.'
  const label = lastProviderLabel()
  const event = label ? `${label} sign-in` : 'Sign-in'
  const who = label || 'your sign-in provider'
  const code = (params.get('error_code') ?? params.get('error') ?? '').toLowerCase()

  const known: Record<string, string> = {
    access_denied: `${event} was cancelled or refused. If your organisation blocks unapproved apps, ask your IT administrator to approve YourDealPulse.`,
    unauthorized_client: `YourDealPulse is not authorised to sign you in with ${who} yet. Tell us and we'll fix the connection.`,
    invalid_client: `YourDealPulse is not authorised to sign you in with ${who} yet. Tell us and we'll fix the connection.`,
    provider_disabled: `${event} is not enabled for YourDealPulse yet.`,
    provider_email_needs_verification: `Verify your email address with ${who}, then sign in again.`,
    validation_failed: `${event} did not return the details we need to identify you — usually a missing email address.`,
    server_error: `${event} could not be completed. This is usually temporary, so try again in a moment.`,
  }
  if (known[code]) return known[code] + fallback

  // No wording of our own: pass the provider's explanation through rather than
  // swallow it, tidied so it reads as a sentence. React escapes it on render.
  let description = (params.get('error_description') ?? '').trim().slice(0, 200)
  if (description && !/[.!?]$/.test(description)) description += '.'
  return `${event} failed${description ? `: ${description}` : '.'}${fallback}`
}

/** What came back on the redirect: a session, a refusal, or neither. */
export type AuthCallback = { ok: boolean; error: string | null }

/**
 * Consume whatever Supabase returned on the redirect and clean the address bar,
 * so the access token is not left in browser history.
 *
 * Failures arrive the same way tokens do — in the URL. Reading only
 * `access_token` left a refused sign-in on a blank login screen with nothing to
 * explain it, so both are read here, from the fragment and the query string.
 */
export function consumeAuthCallback(): AuthCallback {
  const params = new URLSearchParams()
  const hash = window.location.hash
  if (hash.length > 1) {
    new URLSearchParams(hash.slice(1)).forEach((value, key) => params.set(key, value))
  }
  new URLSearchParams(window.location.search).forEach((value, key) => {
    if (key === 'error' || key === 'error_code' || key === 'error_description') params.set(key, value)
  })

  const failed = params.get('error') ?? params.get('error_code')
  const access = params.get('access_token')
  if (!failed && !access) return { ok: false, error: null }

  if (access) {
    setSession(access, params.get('refresh_token'), Number(params.get('expires_in') ?? 3600))
  }
  const message = failed ? oauthErrorMessage(params) : null

  const query = new URLSearchParams(window.location.search)
  for (const key of ['error', 'error_code', 'error_description']) query.delete(key)
  const search = query.toString()
  window.history.replaceState(null, '', window.location.pathname + (search ? `?${search}` : ''))

  return { ok: Boolean(access), error: message }
}
